import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { verificationApi } from '@/api/verification';
import type { JourneyTimelineEvent } from '@/types/verification';
import {
  JOURNEY_EVENT_FALLBACK_ICON,
  JOURNEY_EVENT_ICONS,
  groupEventsByDay,
  isTodayKey,
} from '@/lib/journeyTimeline';
import { useAsyncData } from '@/hooks';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Icon } from '@/components/icons';
import styles from './JourneyPage.module.css';

/**
 * The school-day timeline — a guardian's view of their child's explicit
 * institutional events (arrived, in class, library, bus…), or a member's view
 * of their own.
 *
 * WHOSE DAY: `/journey/:personId` shows that person; the server enforces the
 * scope (own timeline, or a verified academic-access guardian relationship —
 * anything else is a 403 the page shows honestly). Bare `/journey` falls back
 * to the signed-in person's own day via /me.
 *
 * KNOWN GAP, stated rather than papered over: nothing in this app's session or
 * portal contexts exposes a guardian's children as identity personIds yet
 * (/me/relationships returns studentReference strings, and the dashboard
 * projection's child ids are EduCore ids). Until a resolution endpoint exists,
 * a guardian reaches a child's day through a link carrying the personId, and
 * the page says so — there is deliberately no fabricated child selector.
 */

const NS = 'journey';

export function JourneyPage() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').split('-')[0];
  const { personId: routePersonId } = useParams<{ personId: string }>();

  // Without a route param the page shows the caller's own day; /me is the only
  // place the signed-in person's identity-service id lives.
  const { state: selfState, reload: reloadSelf } = useAsyncData<string | null>(
    'journey-self-person',
    () => verificationApi.myPersonId(),
    !routePersonId,
  );

  const personId =
    routePersonId ?? (selfState.status === 'ready' ? selfState.data ?? '' : '');

  const { state: timelineState, reload: reloadTimeline } = useAsyncData<JourneyTimelineEvent[]>(
    `journey-timeline-${personId}`,
    () => verificationApi.timeline(personId, { limit: 200 }),
    !!personId,
  );

  const dayFormatter = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const timeFormatter = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.headerName}>{t(`${NS}.title`)}</h1>
        <p className={styles.headerSubtitle}>{t(`${NS}.subtitle`)}</p>
        {/* The privacy promise, in view wherever the timeline is. */}
        <p className={styles.privacyNote}>{t(`${NS}.privacy`)}</p>
      </header>

      {!routePersonId && (
        <div className={styles.gapNote} role="note">
          {t(`${NS}.selfNote`)}
        </div>
      )}

      {!routePersonId && selfState.status === 'ready' && !selfState.data ? (
        // The signed-in account is not linked to a person — nothing to show,
        // and saying "no events" would be the wrong sentence.
        <div className={styles.gapNote} role="status">
          {t(`${NS}.noPerson`)}
        </div>
      ) : !personId && !routePersonId ? (
        <AsyncPanel state={selfState} onRetry={reloadSelf}>
          {() => null}
        </AsyncPanel>
      ) : (
        <AsyncPanel
          state={timelineState}
          onRetry={reloadTimeline}
          isEmpty={(events) => events.length === 0}
          emptyTitle={t(`${NS}.emptyToday`)}
          emptyBody={t(`${NS}.emptyBody`)}
        >
          {(events) => (
            <div>
              {groupEventsByDay(events).map((group) => (
                <section
                  key={group.dayKey}
                  className={styles.dayGroup}
                  aria-label={dayFormatter.format(group.date)}
                >
                  <h2 className={styles.dayHeading}>
                    {isTodayKey(group.dayKey)
                      ? t(`${NS}.today`)
                      : dayFormatter.format(group.date)}
                  </h2>
                  <ol className={styles.eventList}>
                    {group.events.map((event) => (
                      <li key={event.eventId} className={styles.eventRow}>
                        <span className={styles.eventIcon} aria-hidden="true">
                          <Icon
                            name={
                              JOURNEY_EVENT_ICONS[event.eventType] ?? JOURNEY_EVENT_FALLBACK_ICON
                            }
                            size={16}
                          />
                        </span>
                        <span className={styles.eventBody}>
                          <span className={styles.eventLabel}>
                            {t(`${NS}.event.${event.eventType}`, {
                              defaultValue: event.eventType,
                            })}
                          </span>
                          <span className={styles.eventTime}>
                            {event.occurredAt
                              ? timeFormatter.format(new Date(event.occurredAt))
                              : '—'}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          )}
        </AsyncPanel>
      )}
    </div>
  );
}
